import {testIdProps} from '@github-ui/test-id-props'
import {ActionList, useMergedRefs} from '@primer/react'
import {type CSSProperties, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react'

import {usePrefixedId} from '../hooks/common/use-prefixed-id'
import getPositionOnScreen, {type XEdge} from '../hooks/get-position-on-screen'
import {useActiveDescendant} from '../hooks/use-active-descendant'
import {Resources} from '../strings'
import {useEventHandler} from './board/hooks/use-event-handler'
import {Portal} from './common/portal'
import styles from './suggestions-list.module.css'
import {SuggestionsListItem, type SuggestionsListItemProps} from './suggestions-list-item'

export type SuggestionItems = Array<Omit<SuggestionsListItemProps, 'selected' | 'selectedItemRef' | 'id'>>

interface SuggestionsListProps {
  items: SuggestionItems
  controllingElementRef: React.RefObject<HTMLElement | null>
  inputRef?: React.RefObject<HTMLElement | null>
  listRef: React.Ref<HTMLUListElement>
  xAlign?: XEdge
  xOriginEdgeAlign?: XEdge
  testId: string
  style?: Pick<CSSProperties, 'width' | 'minWidth' | 'maxWidth' | 'marginLeft'>
}

/**
 * A simple list component that can be used to render a list of suggestions.
 * Supports basic keyboard navigation.
 */

const SuggestionsList: React.FC<SuggestionsListProps> = ({
  items,
  controllingElementRef,
  inputRef,
  listRef,
  xAlign,
  xOriginEdgeAlign,
  testId,
  style,
}: SuggestionsListProps) => {
  const [selectedItemIndex, setSelectedItemIndex] = useState<number>(0)

  const getListItemId = useCallback((index: number) => items[index]?.testId, [items])
  useActiveDescendant({inputRef: inputRef ?? controllingElementRef, selectedIndex: selectedItemIndex, getListItemId})

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedItemIndex(0)
  }, [items.length])

  const selectNextItem = useCallback(() => {
    setSelectedItemIndex(idx => (idx == null || idx + 1 === items.length ? 0 : idx + 1))
  }, [items.length])

  const selectPreviousItem = useCallback(() => {
    setSelectedItemIndex(idx => (idx == null || idx === 0 ? items.length - 1 : idx - 1))
  }, [items.length])

  useLayoutEffect(() => {
    selectedItemRef.current?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'nearest'})
  }, [selectedItemIndex])

  const onKeyDown = useEventHandler(
    (e: KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      switch (e.key) {
        case 'ArrowDown':
          selectNextItem()
          return true
        case 'ArrowUp':
          selectPreviousItem()
          return true
        case 'Enter':
          if (items.length) {
            items[selectedItemIndex]?.onSelect()
            return true
          }
          return false
        default:
          return false
      }
    },
    [items, selectNextItem, selectPreviousItem, selectedItemIndex],
  )

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [onKeyDown])

  const selectedItemRef = useRef<HTMLLIElement>(null)
  const portalId = usePrefixedId('__omnibarPortalRoot__')
  return items.length > 0 ? (
    <Portal id={portalId}>
      <List
        items={items}
        controllingElementRef={controllingElementRef}
        listRef={listRef}
        selectedItemIndex={selectedItemIndex}
        selectedItemRef={selectedItemRef}
        xAlign={xAlign}
        xOriginEdgeAlign={xOriginEdgeAlign}
        testId={testId}
        style={style}
      />
    </Portal>
  ) : null
}

function List({
  items,
  controllingElementRef,
  listRef: externalListRef,
  selectedItemIndex,
  selectedItemRef,
  xAlign,
  xOriginEdgeAlign,
  testId,
  style,
}: SuggestionsListProps & {selectedItemIndex: number; selectedItemRef: React.RefObject<HTMLLIElement | null>}) {
  const listRef = useRef<HTMLUListElement>(null)

  const mergedListRef = useMergedRefs(externalListRef, listRef)

  useStyledSuggestionListStyles({
    controllingElementRef,
    items,
    listRef,
    xAlign,
    xOriginEdgeAlign,
  })

  return (
    <div role="dialog" aria-label="Filter suggestions">
      <ActionList
        style={style}
        id={testId}
        aria-label="Results"
        role="listbox"
        ref={mergedListRef}
        {...testIdProps(testId)}
        tabIndex={-1}
        className={styles.ActionList}
      >
        {items.map((item, index) => {
          return (
            <SuggestionsListItem
              id={item.testId}
              // eslint-disable-next-line @eslint-react/no-array-index-key
              key={index}
              value={item.value}
              testId={item.testId}
              asHTML={item.asHTML}
              onSelect={item.onSelect}
              renderItem={item.renderItem}
              selected={selectedItemIndex === index}
              selectedItemRef={selectedItemIndex === index ? selectedItemRef : null}
            />
          )
        })}
      </ActionList>
      <span
        className="sr-only"
        id={`${testId}-feedback`}
        aria-live="polite"
        aria-atomic="true"
        {...testIdProps(`${testId}-feedback`)}
      >
        {Resources.resultCount(items.length)}
      </span>
    </div>
  )
}

/**
 * A hooks that returns a stateful style object
 * used to position the suggestions list
 * so that it is visible on screen after a re-render
 */
function useStyledSuggestionListStyles<T>({
  controllingElementRef,
  items,
  listRef,
  xAlign = 'left',
  xOriginEdgeAlign = 'right',
}: {
  controllingElementRef: React.RefObject<Element | null>
  items: Array<T>
  listRef: React.RefObject<HTMLUListElement | null>
  xAlign?: XEdge
  xOriginEdgeAlign?: XEdge
  style?: CSSProperties
}) {
  useEffect(() => {
    const {current} = listRef
    if (!controllingElementRef.current || !current) {
      return
    }

    const coordinates = getPositionOnScreen({
      originRef: controllingElementRef,
      contentRef: listRef,
      alignment: {xAlign, xOriginEdgeAlign},
    })

    current.style.top = `${coordinates.top}px`
    current.style.left = `${coordinates.left}px`
    current.style.transition = `left 0.1s ease`
  }, [controllingElementRef, items, listRef, xAlign, xOriginEdgeAlign])
}

export default SuggestionsList

SuggestionsList.displayName = 'SuggestionsList'
